import React from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';

import BasicInformationStep from './BasicInformationStep';
import ProfessionalDetailsStep from './ProfessionalDetailsStep';
import WorkLogisticsStep from './WorkLogisticsStep';
import VehicleTravelStep from './VehicleTravelStep';
import TravelPreferencesStep from './TravelPreferencesStep';
import RepresentationStep from './RepresentationStep';
import { FormValues, OnboardingStep } from './types';

interface ReviewStepProps {
	formValues: FormValues;
	control: Control<FormValues>;
	setValue: UseFormSetValue<FormValues>;
	errors: FieldErrors<FormValues>;
	token?: string;
	onDocumentsUpdate?: (payload: Partial<FormValues>) => Promise<void> | void;
}

const reviewSections = (
	formValues: FormValues,
	control: Control<FormValues>,
	setValue: UseFormSetValue<FormValues>,
	errors: FieldErrors<FormValues>,
	token?: string,
	onDocumentsUpdate?: (payload: Partial<FormValues>) => Promise<void> | void
) => [
	{
		step: OnboardingStep.BASIC_INFO,
		title: 'Basic Information',
		description: 'Review who you are and how we can contact you.',
		content: (
			<BasicInformationStep
				formValues={formValues}
				setValue={setValue}
				control={control}
				errors={errors}
			/>
		),
	},
	{
		step: OnboardingStep.PROFESSIONAL_DETAILS,
		title: 'Professional Details',
		description: 'Confirm your roles, experience, and references.',
		content: (
			<ProfessionalDetailsStep
				formValues={formValues}
				setValue={setValue}
				control={control}
				errors={errors}
				token={token}
				onDocumentsUpdate={onDocumentsUpdate}
			/>
		),
	},
	{
		step: OnboardingStep.WORK_LOGISTICS,
		title: 'Work Logistics',
		description: 'Verify work locations, employment, and payment details.',
		content: (
			<WorkLogisticsStep
				formValues={formValues}
				setValue={setValue}
				control={control}
				errors={errors}
			/>
		),
	},
	{
		step: OnboardingStep.VEHICLE_TRAVEL,
		title: 'Vehicle & Travel',
		description: 'Confirm your travel readiness and transportation info.',
		content: (
			<VehicleTravelStep
				formValues={formValues}
				setValue={setValue}
				control={control}
				errors={errors}
			/>
		),
	},
	{
		step: OnboardingStep.TRAVEL_PREFERENCES,
		title: 'Travel Preferences',
		description:
			'Review your airline, hotel, and transportation preferences.',
		content: (
			<TravelPreferencesStep
				formValues={formValues}
				setValue={setValue}
				control={control}
				errors={errors}
			/>
		),
	},
	{
		step: OnboardingStep.REPRESENTATION,
		title: 'Representation',
		description: 'Double-check any agent or management details.',
		content: (
			<RepresentationStep
				formValues={formValues}
				setValue={setValue}
				control={control}
				errors={errors}
			/>
		),
	},
];

export default function ReviewStep(props: ReviewStepProps) {
	const { formValues, control, setValue, errors, token, onDocumentsUpdate } =
		props;
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
			{reviewSections(
				formValues,
				control,
				setValue,
				errors,
				token,
				onDocumentsUpdate
			).map(({ step, title, description, content }, index) => (
				<Accordion
					key={step}
					defaultExpanded={index === 0}
					disableGutters
					sx={{
						backgroundColor: '#242424',
						borderRadius: '8px',
						'&:before': { display: 'none' },
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						sx={{
							backgroundColor: '#242424',
							borderRadius: '8px',
							px: 3,
							py: 2,
						}}
					>
						<Box>
							<Typography
								variant="h6"
								sx={{
									color: 'text.primary',
									fontWeight: 600,
								}}
							>
								{title}
							</Typography>
							{description ? (
								<Typography
									variant="body2"
									sx={{
										color: 'text.secondary',
										mt: 0.5,
									}}
								>
									{description}
								</Typography>
							) : null}
						</Box>
					</AccordionSummary>
					<AccordionDetails
						sx={{
							backgroundColor: '#242424',
							px: 3,
							pb: 3,
						}}
					>
						{content}
					</AccordionDetails>
				</Accordion>
			))}
		</Box>
	);
}
